import { ref, getDownloadURL } from "firebase/storage";
import { functions, httpsCallable, storage } from "../firebase.js";
import { ensureInlinePdfUrl, logPdfDiagnostic } from "./pdfUrlUtils";

const resolvedCache = new Map();
const inFlightCache = new Map();
const pdfBytesCache = new Map();
const pdfBytesInFlightCache = new Map();
let getPdfBoxBytesCallable;

function base64ToUint8Array(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

function getPdfBoxBytesFn() {
  if (!getPdfBoxBytesCallable) {
    getPdfBoxBytesCallable = httpsCallable(functions, "getPdfBoxBytes");
  }
  return getPdfBoxBytesCallable;
}

export function getStoragePathFromDownloadUrl(downloadUrl) {
  try {
    const u = new URL(downloadUrl);
    const encoded = u.pathname.split("/o/")[1] || "";
    return decodeURIComponent(encoded);
  } catch {
    return null;
  }
}

export function isFirebaseStorageUrl(url) {
  return typeof url === "string" && url.includes("firebasestorage.googleapis.com");
}

/**
 * Store a download URL in the session cache (e.g. after upload).
 */
export function seedCachedDownloadUrl(objectPath, url) {
  if (objectPath && url) {
    resolvedCache.set(objectPath, url);
  }
}

/**
 * Resolve a Firebase Storage object path to a download URL with session caching
 * and in-flight deduplication.
 */
export async function getCachedDownloadUrl(objectPath, { forceRefresh = false } = {}) {
  if (!objectPath) {
    throw new Error("objectPath is required");
  }

  if (!forceRefresh && resolvedCache.has(objectPath)) {
    return resolvedCache.get(objectPath);
  }

  if (!forceRefresh && inFlightCache.has(objectPath)) {
    return inFlightCache.get(objectPath);
  }

  const promise = (async () => {
    const fileRef = ref(storage, objectPath);
    const url = await getDownloadURL(fileRef);
    resolvedCache.set(objectPath, url);
    return url;
  })();

  inFlightCache.set(objectPath, promise);

  try {
    return await promise;
  } finally {
    inFlightCache.delete(objectPath);
  }
}

export function invalidateCachedDownloadUrl(objectPath) {
  if (!objectPath) return;
  resolvedCache.delete(objectPath);
  inFlightCache.delete(objectPath);
}

export function invalidateCachedDownloadUrlsForUser(uid) {
  if (!uid) return;
  const prefix = `users/${uid}/`;
  for (const key of [...resolvedCache.keys()]) {
    if (key.startsWith(prefix)) {
      resolvedCache.delete(key);
    }
  }
  for (const key of [...inFlightCache.keys()]) {
    if (key.startsWith(prefix)) {
      inFlightCache.delete(key);
    }
  }
}

/** @internal tests only */
export function clearDownloadUrlCache() {
  resolvedCache.clear();
  inFlightCache.clear();
  pdfBytesCache.clear();
  pdfBytesInFlightCache.clear();
}

/**
 * Download PDF bytes through a callable Cloud Function (Admin SDK), avoiding browser CORS.
 */
function clonePdfBytes(bytes) {
  if (!bytes) {
    return null;
  }

  // pdf.js transfers the underlying ArrayBuffer to its worker, which detaches it.
  // Always hand react-pdf a fresh copy so cached bytes stay usable.
  return bytes.slice();
}

export async function fetchFirebaseStoragePdfBytes(url, diagnosticContext = "pdf-bytes") {
  if (!url || !isFirebaseStorageUrl(url)) {
    return null;
  }

  const objectPath = getStoragePathFromDownloadUrl(url);
  if (!objectPath) {
    throw new Error("Unable to parse Storage path from URL");
  }

  if (pdfBytesCache.has(objectPath)) {
    logPdfDiagnostic(diagnosticContext, "pdf_bytes_cache_hit", { objectPath });
    return clonePdfBytes(pdfBytesCache.get(objectPath));
  }

  if (pdfBytesInFlightCache.has(objectPath)) {
    const masterBytes = await pdfBytesInFlightCache.get(objectPath);
    return clonePdfBytes(masterBytes);
  }

  const promise = (async () => {
    logPdfDiagnostic(diagnosticContext, "pdf_bytes_fetch_start", { objectPath, via: "callable" });
    const response = await getPdfBoxBytesFn()({ objectPath });
    const base64 = response?.data?.base64;
    if (!base64) {
      throw new Error("PDF bytes response was empty");
    }

    const bytes = base64ToUint8Array(base64);
    pdfBytesCache.set(objectPath, bytes);
    logPdfDiagnostic(diagnosticContext, "pdf_bytes_fetch_success", {
      objectPath,
      byteLength: bytes.byteLength,
      via: "callable",
    });
    return bytes;
  })();

  pdfBytesInFlightCache.set(objectPath, promise);

  try {
    const masterBytes = await promise;
    return clonePdfBytes(masterBytes);
  } finally {
    pdfBytesInFlightCache.delete(objectPath);
  }
}

export function isPdfTooLargeForCallableError(error) {
  const code = String(error?.code || "");
  const message = String(error?.message || "");
  return code === "functions/failed-precondition" && /too large/i.test(message);
}

/**
 * Prefer callable bytes (no CORS). If the PDF is over the callable cap, hand
 * PDF.js the Storage URL so it can fetch directly when bucket CORS allows it.
 */
export async function resolvePdfJsDocumentFile(url, diagnosticContext = "pdf-bytes") {
  if (!url) {
    return null;
  }

  if (!isFirebaseStorageUrl(url)) {
    return ensureInlinePdfUrl(url) || url;
  }

  try {
    const bytes = await fetchFirebaseStoragePdfBytes(url, diagnosticContext);
    return { data: bytes };
  } catch (error) {
    if (isPdfTooLargeForCallableError(error)) {
      const inlineUrl = ensureInlinePdfUrl(url);
      logPdfDiagnostic(diagnosticContext, "pdf_bytes_too_large_direct_url", {
        hasInlineUrl: Boolean(inlineUrl),
      });
      if (!inlineUrl) {
        throw error;
      }
      return inlineUrl;
    }

    throw error;
  }
}

/**
 * Returns a fresh download URL for Firebase Storage links; otherwise returns the original URL.
 */
export async function refreshFirebaseStorageUrl(url, diagnosticContext = "storage-url") {
  if (!url || !isFirebaseStorageUrl(url)) {
    logPdfDiagnostic(diagnosticContext, "storage_url_passthrough", {
      isFirebaseStorageUrl: isFirebaseStorageUrl(url),
    });
    return url || null;
  }

  try {
    const objectPath = getStoragePathFromDownloadUrl(url);
    if (!objectPath) throw new Error("Unable to parse Storage path from URL");

    const hadCachedUrl = resolvedCache.has(objectPath);
    const freshUrl = await getCachedDownloadUrl(objectPath);
    logPdfDiagnostic(diagnosticContext, hadCachedUrl ? "storage_url_cache_hit" : "storage_url_refreshed", {
      objectPath,
    });
    return freshUrl;
  } catch (error) {
    console.error("Error refreshing Firebase Storage URL:", error);
    logPdfDiagnostic(diagnosticContext, "storage_url_refresh_failed", {
      message: error?.message,
    });
    return url;
  }
}
